import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight"
import Badge from "@mui/material/Badge"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useState } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { Link as RouterLink } from "react-router-dom"

import Column from "../../../common/components/Column"
import NavLogo from "../../../static/images/sidebar-icons/nav-logo.png"
import { selectProfile } from "../../users/reducer"
import { useLogoutUser } from "../utils"
import SidebarProfileMenu from "./SidebarProfileMenu"
import { useInboxNotificationsContext } from "./inboxNotifications/InboxNotificationsContext"
import {
  getSidebarBottomLinks,
  getSidebarLinkGroups,
} from "./sidebarLinkConfig"
import {
  LinkContainer,
  LinkTypography,
  shouldForwardProp,
} from "./sidebarStyles"

export {
  FREE_SIDEBAR_LINKS,
  SIDEBAR_LINK_GROUPS,
  getSidebarBottomLinks,
  getSidebarLinkGroups,
} from "./sidebarLinkConfig"

export { PROFILE_MENU_URL } from "./SidebarProfileMenu"

export const SIDEBAR_WIDTH_COLLAPSED = "80px"

/** Live counts for links that carry a badgeKey. */
export function useSidebarBadgeCounts() {
  const inbox = useInboxNotificationsContext()
  return { messages: inbox.counts.total }
}

export default function SidebarLinks() {
  const [extended, setExtended] = useState(false)
  const logoutUser = useLogoutUser()
  const profile = useSelector(selectProfile)
  const linkGroups = getSidebarLinkGroups(profile)
  const bottomLinks = getSidebarBottomLinks(profile)
  const badgeCounts = useSidebarBadgeCounts()

  function toggleSidebar() {
    setExtended(!extended)
  }

  function closeSidebar() {
    setExtended(false)
  }

  function handleLogOutClicked() {
    closeSidebar()
    logoutUser()
  }

  return (
    <LinksColumn extended={extended}>
      <Link
        component={RouterLink}
        to="/"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
        onClick={closeSidebar}
      >
        <img
          src={NavLogo}
          style={{ borderRadius: "8px", width: "44px" }}
          alt="OneBase square logo"
        />
        {extended && (
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              color: "white",
            }}
          >
            OneBase
          </Typography>
        )}
      </Link>
      <Column
        sx={{
          mt: 3,
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
          minHeight: 0,
        }}
      >
        <GroupsColumn>
          {linkGroups.map((group, groupIndex) => (
            <LinkGroupColumn key={group.label} extended={extended}>
              {extended ? (
                <GroupLabel>{group.label}</GroupLabel>
              ) : groupIndex > 0 ? (
                <GroupDivider />
              ) : null}
              {group.links.map((sidebarLink) => (
                <MuiIconSidebarLink
                  key={sidebarLink.url}
                  linkData={sidebarLink}
                  onClick={closeSidebar}
                  extended={extended}
                  badgeCount={badgeCounts[sidebarLink.badgeKey]}
                />
              ))}
            </LinkGroupColumn>
          ))}
        </GroupsColumn>
        <Column sx={{ gap: 2, position: "relative", pt: 2 }}>
          <Divider sx={{ borderColor: "#F5EEFE", opacity: 0.1 }} />
          <OpenCloseIconButton
            extended={extended}
            onClick={toggleSidebar}
            aria-label={extended ? "Collapse sidebar" : "Expand sidebar"}
          >
            <OpenCloseArrowsIcon extended={extended} />
          </OpenCloseIconButton>
          <LinkGroupColumn extended={extended}>
            {bottomLinks.map((sidebarLink) => (
              <MuiIconSidebarLink
                key={sidebarLink.url}
                linkData={sidebarLink}
                onClick={closeSidebar}
                extended={extended}
              />
            ))}
          </LinkGroupColumn>
          <SidebarProfileMenu
            extended={extended}
            onNavigate={closeSidebar}
            onLogout={handleLogOutClicked}
          />
        </Column>
      </Column>
    </LinksColumn>
  )
}

function MuiIconSidebarLink({ linkData, onClick, extended, badgeCount }) {
  const isActive = useIsPathActive(linkData.url)
  const showBadge = Boolean(badgeCount)
  return (
    <LinkContainer
      component={RouterLink}
      to={linkData.url}
      title={showBadge ? `${linkData.title} (${badgeCount})` : linkData.title}
      isActive={isActive}
      extended={extended}
      onClick={onClick}
      {...linkData.getLinkProps()}
    >
      <Badge
        color="secondary"
        badgeContent={badgeCount}
        invisible={extended || !showBadge}
        max={99}
        sx={{ "& .MuiBadge-badge": { fontSize: "10px", height: 16 } }}
      >
        <linkData.icon sx={{ color: "white" }} />
      </Badge>
      <LinkTypography extended={extended}>{linkData.title}</LinkTypography>
      {extended && showBadge ? (
        <CountPill>{badgeCount > 99 ? "99+" : badgeCount}</CountPill>
      ) : null}
    </LinkContainer>
  )
}

export function useIsPathActive(to) {
  const location = useLocation()
  return location.pathname.startsWith(to)
}

const GroupsColumn = styled(Column)(({ theme }) => ({
  gap: theme.spacing(1.5),
  width: "100%",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "none",
}))

const LinkGroupColumn = styled(
  Column,
  shouldForwardProp
)(({ extended }) => ({
  gap: "2px",
  width: "100%",
}))

const GroupLabel = styled(Typography)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.6)",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  paddingInline: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.5),
}))

const GroupDivider = styled(Divider)(({ theme }) => ({
  borderColor: "rgba(255, 255, 255, 0.18)",
  width: "32px",
  alignSelf: "center",
  marginBottom: theme.spacing(1),
}))

const LinksColumn = styled(
  Column,
  shouldForwardProp
)(({ theme, extended }) => ({
  paddingInline: "14px",
  paddingBlock: theme.spacing(3),
  alignItems: extended ? "flex-start" : "center",
  position: "sticky",
  top: 0,
  left: 0,
  width: extended ? "268px" : SIDEBAR_WIDTH_COLLAPSED,
  height: "100vh",
  minHeight: "680px",
  background:
    "linear-gradient(159deg, rgba(137, 47, 206, 1) 3%, rgba(137, 47, 206, 0.8) 100%)",
  backdropFilter: "blur(12px)",
  transition: theme.transitions.create(["width"], {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.shortest,
  }),
  [theme.breakpoints.only("xs")]: {
    display: "none",
  },
  zIndex: 100,
}))

const CountPill = styled("span")(({ theme }) => ({
  marginLeft: "auto",
  minWidth: "22px",
  height: "20px",
  paddingInline: "6px",
  borderRadius: "10px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 700,
  color: theme.palette.primary.main,
  backgroundColor: "white",
}))

const OpenCloseIconButton = styled(
  IconButton,
  shouldForwardProp
)(({ theme, extended }) => ({
  width: "40px",
  height: "40px",
  backgroundColor: extended
    ? theme.palette.primary.main
    : theme.palette.secondary.main,
  position: "absolute",
  right: "-35px",
  top: "-4px",
  "&:hover": {
    backgroundColor: extended
      ? theme.palette.primary.main
      : theme.palette.secondary.main,
  },
}))

const OpenCloseArrowsIcon = styled(
  KeyboardDoubleArrowRightIcon,
  shouldForwardProp
)(({ theme, extended }) => ({
  fill: "white",
  transform: extended ? "rotate(180deg)" : "none",
  transition: theme.transitions.create("transform"),
}))
